/**
 * W21 — interact-object effect router.
 *
 * An `interact` Tiled object (lever, button, pressure plate, shrine) now
 * carries an `effect` discriminant. Clicking or stepping on one routes
 * through this resolver, which translates the effect into a runtime
 * directive the caller can dispatch: teleport the party, toggle a door,
 * or fire an Ink knot.
 *
 * Pure function: no `EventBus` emission, no store writes. The main-loop
 * caller turns the returned directive into the right engine action (so
 * unit tests can drive this without a Three.js scene).
 *
 * Dangling teleporter links return a `no_op` directive so authoring
 * mistakes degrade gracefully — the feed can surface a "nothing
 * happens" line rather than crashing.
 */

import type { DungeonMap, Position } from "../types/grid.js";
import type { InteractEffect } from "../types/tiled.js";
import { resolveInteract } from "../loaders/TiledLoader.js";

export type InteractRouterResult =
  | {
      readonly kind: "teleport";
      readonly targetMapId: string;
      readonly targetCell: Position;
      readonly teleporterId: string;
    }
  | { readonly kind: "open_door"; readonly doorId: string }
  | { readonly kind: "fire_trigger"; readonly inkKnot: string }
  | { readonly kind: "toggle_light"; readonly lightIds: readonly string[] }
  | { readonly kind: "open_camp_stash" }
  | { readonly kind: "no_op"; readonly reason: "dangling_teleporter" };

export function routeInteractEffect(
  effect: InteractEffect,
  map: DungeonMap,
): InteractRouterResult {
  switch (effect.kind) {
    case "teleport": {
      const tp = resolveInteract(map, effect.teleporter_id);
      if (tp === null) return { kind: "no_op", reason: "dangling_teleporter" };
      return {
        kind: "teleport",
        targetMapId: tp.targetMapId,
        targetCell: tp.targetCell,
        teleporterId: effect.teleporter_id,
      };
    }
    case "open_door":
      return { kind: "open_door", doorId: effect.door_id };
    case "fire_trigger":
      return { kind: "fire_trigger", inkKnot: effect.ink_knot };
    case "toggle_light":
      return { kind: "toggle_light", lightIds: effect.light_ids };
    case "open_camp_stash":
      return { kind: "open_camp_stash" };
  }
}
